// The branch's four steps on the Steps card (spec C8, S17), in place of steps 1 to 6 while the
// renewal is on Non-renewal or Policy inactive. Same rules as the renewal steps: they open in order,
// a tick stamps who and when and writes the Portal log, and only the last step ticked can be
// unticked (who ticked it the same day, or the Manager). Markup from design/kit (StepCard, Checkbox,
// Button) and the Record board's step parts (logrow, okbox, frow).
import { useRef, useState, type ReactNode } from 'react';
import type {
  Api,
  BranchStepKey,
  BranchTick,
  BranchView,
  RenewalDetail,
  StepsView,
} from '../auth/api';
import { Tip } from '../shell/Tip';
import {
  CheckIcon,
  CopyIcon,
  ExternalLinkIcon,
  FileIcon,
  InfoIcon,
  SendIcon,
  UploadIcon,
} from '../shell/icons';
import {
  branchLine,
  branchRefusal,
  branchStepTitles,
  branchStepWhy,
  branchTickText,
  handLine,
} from './branch-lines';
import { EmailStep } from './EmailStep';
import { stepStamp } from './step-lines';
import { pickProblem } from './upload-lines';

type Props = {
  api: Api;
  renewal: RenewalDetail;
  view: StepsView & { branch: BranchView };
  /** The steps as the server answered after a tick, an untick or an upload. */
  onView: (v: StepsView) => void;
  /** A toast, with Undo of this step when given. */
  say: (text: string, undo?: BranchStepKey) => void;
  /** Hand to AE with the reason Non-renewal (spec C8 non-renewal step 4). */
  onHand?: () => void;
  /** Mark as sent was pressed: the steps are read again (the send ticks step 2). */
  onSent: () => void;
  revision: string;
};

export function BranchSteps({ api, renewal, view, onView, say, onHand, onSent, revision }: Props) {
  const b = view.branch;
  const [busy, setBusy] = useState(false);
  async function tick(t: BranchTick) {
    setBusy(true);
    const res = await api.tickBranchStep(renewal.id, t);
    setBusy(false);
    const sent = res.body.steps as StepsView | undefined;
    if (sent) onView(sent);
    if (res.ok) say(branchTickText(t.step), t.step);
    else say(branchRefusal(res.error));
    return res.ok;
  }
  const step = (key: BranchStepKey) => b.steps.find((s) => s.key === key)!;
  const n = (key: BranchStepKey) => b.steps.findIndex((s) => s.key === key) + 1;
  const undoable = (key: BranchStepKey) => b.last === key && b.canUndo && view.canWork;
  const untick = async (key: BranchStepKey) => {
    const res = await api.undoBranchStep(renewal.id, key);
    const sent = res.body.steps as StepsView | undefined;
    if (sent) onView(sent);
    say(res.ok ? `${branchStepTitles[key]} unticked.` : branchRefusal(res.error));
  };
  const [notice, email, activity, last] = b.steps.map((s) => s.key);
  return (
    <>
      <div className="bnr info" role="status">
        <InfoIcon />
        <span>{branchLine(b)}</span>
      </div>
      <BranchShell
        b={b}
        k={notice}
        n={1}
        onUntick={undoable(notice) ? () => void untick(notice) : null}
      >
        <NoticeBody
          api={api}
          renewal={renewal}
          b={b}
          k={notice}
          canWork={view.canWork && !busy}
          onTick={tick}
          onView={onView}
          say={say}
        />
      </BranchShell>
      <EmailStep
        key={`${renewal.id}:${b.setAt}`}
        api={api}
        renewal={renewal}
        ready={step(email).state !== 'locked'}
        closed={view.close.renewalStatus === 'cancelled' || view.close.renewalStatus === 'done'}
        revision={`${revision}|${b.setAt}`}
        step={step(email)}
        number={2}
        title={branchStepTitles[email]}
        line={branchStepWhy(email, b)}
        lockedText="Opens when the notice is uploaded"
        onSent={onSent}
        onMarkDone={() => void tick({ step: email as 'nr_email' | 'pi_email' })}
        undo={undoable(email) ? () => void untick(email) : null}
      />
      <BranchShell
        b={b}
        k={activity}
        n={n(activity)}
        tag={<span className="tag t-grey">Automated in Phase 2</span>}
        onUntick={undoable(activity) ? () => void untick(activity) : null}
      >
        <ActivityBody
          view={view}
          b={b}
          k={activity}
          canWork={view.canWork && !busy}
          say={say}
          onTick={tick}
        />
      </BranchShell>
      <BranchShell
        b={b}
        k={last}
        n={4}
        stamp={last === 'nr_hand' ? handLine(b) : undefined}
        onUntick={undoable(last) ? () => void untick(last) : null}
      >
        {last === 'nr_hand' && step(last).state === 'open' && view.canWork && onHand && (
          <div className="row g8 wrap">
            <button
              type="button"
              className="btn btn-primary"
              aria-haspopup="dialog"
              onClick={onHand}
            >
              <SendIcon className="ico s14" />
              Hand to AE
            </button>
            <span className="t12 muted">Reason Non-renewal, filled in.</span>
          </div>
        )}
        {last === 'pi_close' && step(last).state === 'open' && (
          <CloseBody b={b} canWork={view.canWork && !busy} onTick={tick} />
        )}
      </BranchShell>
    </>
  );
}

/** The step header (kit StepCard): number or check, title, why, stamp; the body while open. */
function BranchShell({
  b,
  k,
  n,
  tag,
  stamp,
  onUntick,
  children,
}: {
  b: BranchView;
  k: BranchStepKey;
  n: number;
  tag?: ReactNode;
  stamp?: string | null;
  onUntick: (() => void) | null;
  children?: ReactNode;
}) {
  const s = b.steps.find((x) => x.key === k)!;
  const done = s.state === 'done';
  const now = s.state === 'open';
  const title = branchStepTitles[k];
  const shownStamp = stamp ?? (s.stamp ? stepStamp(s) : null);
  return (
    <div
      className={done ? 'step' : now ? 'step now' : 'step locked'}
      aria-disabled={!done && !now}
      aria-label={`Step ${n}, ${title}`}
    >
      <div className="step-h">
        <span className={done ? 'num num-done' : now ? 'num num-now' : 'num num-next'}>
          {done ? <CheckIcon className="ico s12" /> : n}
        </span>
        <span className="st-t">
          <span className="st-tr">
            <span className={done ? 'st-title' : now ? 'st-title now' : 'st-title next'}>
              {title}
            </span>
            {tag}
          </span>
          <span className="st-desc t12 muted">
            {!done && !now ? 'Opens when the step before is done.' : branchStepWhy(k, b)}
          </span>
        </span>
        {done && shownStamp && <span className="stamp ok">{shownStamp}</span>}
      </div>
      {now && children && <div className="step-b">{children}</div>}
      {done && onUntick && (
        <div className="step-b">
          <div className="row endj">
            <button type="button" className="linkbtn" onClick={onUntick}>
              Untick this step
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Step 1: the notices on the record to use, Upload PDF, and for Policy inactive "No notice". */
function NoticeBody({
  api,
  renewal,
  b,
  k,
  canWork,
  onTick,
  onView,
  say,
}: {
  api: Api;
  renewal: RenewalDetail;
  b: BranchView;
  k: BranchStepKey;
  canWork: boolean;
  onTick: (t: BranchTick) => Promise<boolean>;
  onView: (v: StepsView) => void;
  say: (text: string) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const use = (documentId: string) => void onTick({ step: k as 'nr_notice', documentId });
  async function upload(file: File) {
    const problem = pickProblem(file);
    if (problem) return say(problem);
    setUploading(true);
    const res = await api.uploadDocument(renewal.id, file, file.name, 'notice');
    if (!res.ok) {
      setUploading(false);
      return say('Could not upload the notice. Try again.');
    }
    const ok = await onTick({ step: k as 'nr_notice', documentId: res.body.documentId });
    setUploading(false);
    if (!ok) onView(await api.steps(renewal.id));
  }
  return (
    <>
      {b.notices.length > 0 && (
        <div className="okbox">
          {b.notices.map((d) => (
            <div key={d.id} className="frow">
              <span className="file">
                <FileIcon className="ico s14" />
                {d.fileName}
              </span>
              <span className="ml-auto row g6">
                <a
                  className="btn btn-sm"
                  href={api.documentUrl(d.id, false)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View
                </a>
                {canWork && (
                  <button type="button" className="btn btn-sm" onClick={() => use(d.id)}>
                    Use this notice
                  </button>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
      {canWork && (
        <div className="row g8 wrap">
          <input
            ref={input}
            type="file"
            accept="application/pdf"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (f) void upload(f);
            }}
          />
          <button
            type="button"
            className="btn btn-primary"
            disabled={uploading}
            onClick={() => input.current?.click()}
          >
            <UploadIcon className="ico s14" />
            {uploading ? 'Uploading' : 'Upload PDF'}
          </button>
          {k === 'pi_notice' && (
            <button
              type="button"
              className="btn"
              disabled={uploading}
              onClick={() => void onTick({ step: 'pi_notice', none: true })}
            >
              No notice available
            </button>
          )}
        </div>
      )}
    </>
  );
}

/** Step 3: Open in AMS360, the description and the policy number to copy, Email dragged in. */
function ActivityBody({
  view,
  b,
  k,
  canWork,
  say,
  onTick,
}: {
  view: StepsView;
  b: BranchView;
  k: BranchStepKey;
  canWork: boolean;
  say: (text: string) => void;
  onTick: (t: BranchTick) => Promise<boolean>;
}) {
  const [dragged, setDragged] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const copy = async (key: string, value: string, what: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(key);
      say(`${what} copied.`);
    } catch {
      say('Could not copy. Select the text and copy it by hand.');
    }
  };
  const dragLabel = k === 'pi_activity' ? 'Email to the AE attached' : 'Email dragged in';
  const box = (
    <label className="ckline">
      <input
        type="checkbox"
        checked={false}
        disabled={!dragged || !canWork}
        onChange={() =>
          void onTick({ step: k as 'nr_activity' | 'pi_activity', emailDraggedIn: true })
        }
      />
      Logged in AMS360
    </label>
  );
  return (
    <>
      <div className="row g8 wrap">
        <a
          className="btn btn-sm"
          href={view.links.customer}
          target="_blank"
          rel="noopener noreferrer"
        >
          <ExternalLinkIcon className="ico s12" />
          Open in AMS360
        </a>
        <button
          type="button"
          className="btn btn-sm"
          onClick={() => void copy('pol', b.activity.policyNumber, 'Policy number')}
        >
          <CopyIcon className="ico s12" />
          {copied === 'pol' ? 'Copied' : 'Copy policy number'}
        </button>
        <span className="mono t12 muted">{b.activity.policyNumber}</span>
      </div>
      <div className="logrow">
        <span className="t12 muted">Description to paste</span>
        <span className="mono ink grow">{b.activity.description}</span>
        <button
          type="button"
          className="btn btn-sm"
          onClick={() => void copy('log', b.activity.description, 'Description')}
        >
          {copied === 'log' ? 'Copied' : 'Copy'}
        </button>
      </div>
      <div className="row g8 endj">
        <label className="ckline">
          <input
            type="checkbox"
            checked={dragged}
            disabled={!canWork}
            onChange={() => setDragged(!dragged)}
          />
          {dragLabel}
        </label>
        {dragged ? (
          box
        ) : (
          <Tip
            text={`Drag the email into the AMS360 activity first, then tick ${dragLabel}.`}
            focusable
          >
            {box}
          </Tip>
        )}
      </div>
    </>
  );
}

/** Policy inactive step 4: the sheet comment (editable) and Close as Cancelled (spec C8). */
function CloseBody({
  b,
  canWork,
  onTick,
}: {
  b: BranchView;
  canWork: boolean;
  onTick: (t: BranchTick) => Promise<boolean>;
}) {
  const [comment, setComment] = useState(b.sheetComment);
  const text = comment.trim();
  return (
    <>
      <label className="col g4">
        <span className="flab">Sheet comment</span>
        <input
          className="input"
          maxLength={300}
          value={comment}
          disabled={!canWork}
          onChange={(e) => setComment(e.target.value)}
        />
      </label>
      <div className="row g8 endj">
        <span className="t12 muted">Status becomes Cancelled. Undo puts it back.</span>
        <button
          type="button"
          className="btn btn-primary"
          disabled={!canWork || !text}
          onClick={() => void onTick({ step: 'pi_close', sheetComment: text })}
        >
          Close as Cancelled
        </button>
      </div>
    </>
  );
}
